import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/tpo-roof.js';



export default function TPORoofPage() {
  const service = {
    title: 'TPO Roofing for Modern Protection',
    image: '/hero/best-tpo-roof-services-in-los-angeles.webp',
    description:
      'TPO roofing installation by Modern Build. Energy-efficient and cost-effective solutions. Free estimate: (323) 300-4130',
    link: '/services/tpo-roof',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "TPO Roof Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>TPO Roof Installation in Los Angeles | Free Estimate | Modern Build</title>
        <meta name="title" content="TPO Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="TPO roofing installation by Modern Build. Energy-efficient and cost-effective solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="TPO roof Los Angeles, thermoplastic roofing, TPO membrane installation, white reflective roofing, flat roof TPO"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/tpo-roof" />

        <meta property="og:title" content="TPO Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="TPO roofing installation by Modern Build. Energy-efficient and cost-effective solutions. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-tpo-roof-services-in-los-angeles.webp"
        />
        <meta property="og:url" content="https://modernbuildremodel.com/services/tpo-roof" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TPO Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="TPO roofing installation by Modern Build. Energy-efficient and cost-effective solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-tpo-roof-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'TPO Roof Installation in Los Angeles | Free Estimate | Modern Build',
                description:
                  'TPO roofs offer reliable waterproofing and energy savings. Modern Build installs high-quality TPO membrane roofing throughout Los Angeles.',
                slug: '/services/tpo-roof',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="tpoRoof" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="HIGH PERFORMANCE MEMBRANES"
        title="TPO Roof Installation in Los Angeles"
        background="white"
        description="TPO roofing offers excellent durability, reflectivity, and weather resistance. It’s ideal for flat or low-slope roofs in both residential and commercial properties."
        extraText="We use top-grade membranes and expert sealing techniques to ensure long-term performance."
        phone="(323) 300-4130"
        image1="/split/tpo-roof-los-angeles.webp"
        image2="/split/tpo-roof-installation-los-angeles.webp"
      />

<Benefits
  title="Why Choose TPO Roofing in Los Angeles?"
  subtitle="Efficient, economical, and environmentally friendly"
  items={[
    {
      icon: <ShieldCheck className="w-8 h-8 text-primary" />,
      title: "UV & Heat Reflective Surface",
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      title: "Welded Seams for Waterproofing",
    },
    {
      icon: <Wrench className="w-8 h-8 text-primary" />,
      title: "Ideal for Flat & Low-Slope Roofs",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Cost-Effective Installation",
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-primary" />,
      title: "Low Maintenance Requirements",
    },
    {
      icon: <Home className="w-8 h-8 text-primary" />,
      title: "Great for Homes & Commercial Properties",
    },
    {
      icon: <Ruler className="w-8 h-8 text-primary" />,
      title: "Custom-Fit for Maximum Efficiency",
    },
    {
      icon: <CalendarClock className="w-8 h-8 text-primary" />,
      title: "Fast Turnaround with Long-Term Results",
    },
  ]}
/>



      <ContentBlock
        subtitle="SUSTAINABLE & RELIABLE"
        title="Modern TPO Roofing Systems"
        description="Our TPO systems are white, reflective, and ENERGY STAR® rated, helping reduce energy bills while protecting your home or business."
        extraText="We manage everything from roof prep to membrane welding, flashing, and final inspections."
        buttonText="Get a Free TPO Roof Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="TPO Roofing Installation Process"
        subtitle="Installed with expert precision"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "On-Site Roof Evaluation",
            description: "We inspect the roof surface, slope, and drainage to design the right TPO system.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Membrane Planning",
            description: "We plan layout and welding seams, then prepare insulation and substrate as needed.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Installation",
            description: "We heat-weld all seams and apply edge flashing to prevent moisture infiltration.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Inspection & Warranty",
            description: "We complete a quality inspection and provide a warranty on materials and workmanship.",
          },
        ]}
      />

      <Split
        subtitle="ENERGY-EFFICIENT AND CODE COMPLIANT"
        title="Built for Los Angeles Weather"
        background="white"
        description="TPO roofing is durable against UV exposure, heat waves, and heavy rain. With proper installation, it’s one of the best options for flat roofing in California."
        extraText="Ask about reflective coating upgrades and rebate eligibility."
        phone="(323) 300-4130"
        image1="/split/tpo-commercial-roofing-los-angeles.webp"
        image2="/split/tpo-white-roof-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Get a Free TPO Roofing Consultation"
        subtitle="Expertly installed across Los Angeles"
        buttonText="Request Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="tpo-roof" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
